import React, { useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Alert, Box, Button, Grid } from '@mui/material';
import { AgentActivityLogReviewStatus } from '@trnsact/trnsact-shared-types';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { buildFieldEditGroups } from './lib/fieldGroups';
import { AddSubmissionToExistingOpportunityModal } from './components/AddSubmissionToExistingOpportunityModal';
import { UpdateExistingSubmissionModal } from './components/UpdateExistingSubmissionModal';
import { CreateNewOpportunitySubmissionModal } from './components/CreateNewOpportunitySubmissionModal';
import { ExtractedFieldsPanel } from './components/ExtractedFieldsPanel';
import { OriginalDocumentPanel } from './components/OriginalDocumentPanel';
import { ReviewActionsBar } from './components/ReviewActionsBar';
import { ReviewHeader } from './components/ReviewHeader';
import {
  AGENT_ACTIVITY_LOG_REVIEW_CONFIRM,
  AGENT_ACTIVITY_LOG_REVIEW_REJECT,
  ConfirmAgentActivityLogReviewResponse,
  RejectAgentActivityLogReviewResponse,
} from './queries';
import { ExtractionRecord, LenderReviewLocationState, PayloadAttachment } from './types';

export const LenderApplicationReviewPage = () => {
  const history = useHistory();
  const { activityId } = useParams<{ activityId: string }>();
  const location = useLocation<LenderReviewLocationState | undefined>();
  const activity = location.state?.activity;

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmReview, { loading: confirming }] = useMutation<ConfirmAgentActivityLogReviewResponse>(
    AGENT_ACTIVITY_LOG_REVIEW_CONFIRM
  );
  const [rejectReview, { loading: rejecting }] = useMutation<RejectAgentActivityLogReviewResponse>(
    AGENT_ACTIVITY_LOG_REVIEW_REJECT
  );
  const submitting = confirming || rejecting;

  const extraction = (activity?.payload?.detailed_extraction as ExtractionRecord | null | undefined) ?? null;
  const attachments = Array.isArray(activity?.payload?.attachments)
    ? (activity!.payload!.attachments as PayloadAttachment[])
    : [];
  const overallConfidence = (activity?.trigger?.orchestrator_confidence_score as number | undefined) ?? null;
  const reviewReasons = activity?.reviewReasons ?? [];
  const dealerNotMatched = reviewReasons.includes('DEALER_NOT_MATCHED');
  const noDynamicsId = reviewReasons.includes('NO_DYNAMICS_ID');
  const existingOpportunityFound = reviewReasons.includes('EXISTING_OPPORTUNITY_FOUND');
  const existingSubmissionFound = reviewReasons.includes('EXISTING_SUBMISSION_FOUND');
  const showCreateConfirmModal = ['CREATION_FAILED', 'NO_DYNAMICS_ID', 'LOW_CONFIDENCE_EXTRACTION'].some(reason =>
    reviewReasons.includes(reason)
  );
  const existingVendorOpportunityId = (activity?.associations?.vendor_opportunity_id as string | undefined) ?? null;
  const existingCreditSubmissionId = (activity?.associations?.credit_submission_id as string | undefined) ?? null;
  const formId = (activity?.associations?.form_id as string | undefined) ?? null;
  const existingSubmissionMatches =
    (activity?.payload?.existing_submission_matches as
      | Array<{ lenderName?: string | null; borrowerName?: string | null; score?: number }>
      | undefined) ?? [];
  const existingSubmissionMatch = existingSubmissionMatches.length === 1 ? existingSubmissionMatches[0] : null;
  const [selectedDealerDynamicsId, setSelectedDealerDynamicsId] = useState('');
  const [existingOpportunityModalOpen, setExistingOpportunityModalOpen] = useState(false);
  const [existingSubmissionModalOpen, setExistingSubmissionModalOpen] = useState(false);
  const [createConfirmModalOpen, setCreateConfirmModalOpen] = useState(false);

  const opportunityMatchConfidence =
    (activity?.payload?.existing_opportunity_match as { score?: number } | undefined)?.score ?? null;
  const submissionMatchConfidence = existingSubmissionMatch?.score ?? null;
  const existingSubmissionLinkUrl = existingVendorOpportunityId
    ? existingCreditSubmissionId
      ? `/vo/${existingVendorOpportunityId}?creditSubmissionId=${existingCreditSubmissionId}#submissions`
      : `/vo/${existingVendorOpportunityId}#submissions`
    : null;
  const applicantName =
    (extraction?.businessName?.value as string | undefined) ||
    [extraction?.firstName?.value, extraction?.lastName?.value].filter(Boolean).join(' ') ||
    null;
  const contactName =
    [extraction?.firstName?.value, extraction?.lastName?.value].filter(Boolean).join(' ') ||
    (extraction?.email?.value as string | undefined) ||
    null;
  const equipmentDescription = (extraction?.ccg_transaction_equipmentdescription?.value as string | undefined) ?? null;
  const requestedAmount = (extraction?.amountRequested?.value as number | undefined) ?? null;
  const dealerName = (activity?.payload?.dealer_analysis as { dealerName?: string } | undefined)?.dealerName ?? null;

  const groups = useMemo(() => buildFieldEditGroups(extraction, edits), [extraction, edits]);
  const editedCount = useMemo(
    () => groups.reduce((count, group) => count + group.fields.filter(field => field.isDirty).length, 0),
    [groups]
  );

  const handleChange = (key: string, value: string) => setEdits(prev => ({ ...prev, [key]: value }));

  const handleRevert = (key: string) =>
    setEdits(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const handleSubmit = async (status: AgentActivityLogReviewStatus) => {
    if (!activityId) return;
    setSubmitError(null);
    try {
      const fieldEdits = existingOpportunityFound ? [] : Object.entries(edits).map(([key, value]) => ({ key, value }));
      if (status === AgentActivityLogReviewStatus.Confirmed && dealerNotMatched) {
        fieldEdits.push({ key: 'vendorGUID', value: selectedDealerDynamicsId });
      }
      const response =
        status === AgentActivityLogReviewStatus.Confirmed
          ? await confirmReview({
              variables: {
                input: {
                  agentActivityLogId: activityId,
                  fieldEdits,
                },
              },
            })
          : await rejectReview({ variables: { input: { agentActivityLogId: activityId } } });

      const result =
        status === AgentActivityLogReviewStatus.Confirmed
          ? (response.data as ConfirmAgentActivityLogReviewResponse | undefined)?.agentActivityLogReview.confirm
          : (response.data as RejectAgentActivityLogReviewResponse | undefined)?.agentActivityLogReview.reject;

      if (result && !result.success) {
        setSubmitError(result.error ?? 'Something went wrong while saving this review.');
        return;
      }

      history.push('/assistant-log');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong while saving this review.');
    }
  };

  if (!activity) {
    return (
      <PageContainer title="Review Extracted Terms">
        <Box sx={{ p: 3 }}>
          <Alert severity="info">Open this screen from an activity in the Assistant Log.</Alert>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Review Extracted Terms">
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: 'common.white' }}>
        <ReviewHeader overallConfidence={overallConfidence} />

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {!dealerNotMatched && noDynamicsId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This application was created but couldn't be synced to Dynamics automatically — please verify it before
            confirming.
          </Alert>
        )}

        {existingOpportunityFound && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              existingVendorOpportunityId && (
                <Button color="inherit" size="small" onClick={() => history.push(`/vo/${existingVendorOpportunityId}`)}>
                  Open Vendor Opportunity
                </Button>
              )
            }
          >
            We found a matching existing Vendor Opportunity for this applicant — no new credit application was created.
            Use the extracted data below as reference to create a new submission on that opportunity.
          </Alert>
        )}

        {existingSubmissionFound && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              existingVendorOpportunityId && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    history.push(
                      existingCreditSubmissionId
                        ? `/vo/${existingVendorOpportunityId}?creditSubmissionId=${existingCreditSubmissionId}#submissions`
                        : `/vo/${existingVendorOpportunityId}#submissions`
                    )
                  }
                >
                  Open Submission
                </Button>
              )
            }
          >
            {existingSubmissionMatch
              ? `We found a matching existing submission${
                  existingSubmissionMatch.lenderName ? ` (${existingSubmissionMatch.lenderName}` : ''
                }${existingSubmissionMatch.borrowerName ? ` — ${existingSubmissionMatch.borrowerName}` : ''}${
                  existingSubmissionMatch.lenderName ? ')' : ''
                } — decide whether to use the extracted data below to update it instead of creating a new one.`
              : 'We found possible matching existing submissions for this applicant — open the submission to decide whether to use the extracted data below to update it instead of creating a new one.'}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ExtractedFieldsPanel groups={groups} onChange={handleChange} onRevert={handleRevert} />
          </Grid>
          <Grid item xs={12} md={6}>
            <OriginalDocumentPanel agentActivityLogId={activityId} attachments={attachments} />
          </Grid>
        </Grid>

        <ReviewActionsBar
          editedCount={editedCount}
          submitting={submitting}
          onConfirm={() => {
            if (existingOpportunityFound) {
              setExistingOpportunityModalOpen(true);
              return;
            }
            if (existingSubmissionMatch) {
              setExistingSubmissionModalOpen(true);
              return;
            }
            if (showCreateConfirmModal || dealerNotMatched) {
              setCreateConfirmModalOpen(true);
              return;
            }
            handleSubmit(AgentActivityLogReviewStatus.Confirmed);
          }}
          onReject={() => handleSubmit(AgentActivityLogReviewStatus.Rejected)}
        />

        <AddSubmissionToExistingOpportunityModal
          isOpen={existingOpportunityModalOpen}
          submitting={submitting}
          onClose={() => setExistingOpportunityModalOpen(false)}
          onConfirm={() => {
            setExistingOpportunityModalOpen(false);
            handleSubmit(AgentActivityLogReviewStatus.Confirmed);
          }}
          matchConfidence={opportunityMatchConfidence}
          vendorOpportunityId={existingVendorOpportunityId}
          applicantName={applicantName}
          contactName={contactName}
          equipmentDescription={equipmentDescription}
          requestedAmount={requestedAmount}
          dealerName={dealerName}
        />

        <UpdateExistingSubmissionModal
          isOpen={existingSubmissionModalOpen}
          submitting={submitting}
          onClose={() => setExistingSubmissionModalOpen(false)}
          onConfirm={() => {
            setExistingSubmissionModalOpen(false);
            handleSubmit(AgentActivityLogReviewStatus.Confirmed);
          }}
          matchConfidence={submissionMatchConfidence}
          linkUrl={existingSubmissionLinkUrl}
          applicantName={applicantName}
          contactName={contactName}
          equipmentDescription={equipmentDescription}
          requestedAmount={requestedAmount}
          dealerName={dealerName}
        />

        <CreateNewOpportunitySubmissionModal
          isOpen={createConfirmModalOpen}
          submitting={submitting}
          onClose={() => setCreateConfirmModalOpen(false)}
          onConfirm={() => {
            setCreateConfirmModalOpen(false);
            handleSubmit(AgentActivityLogReviewStatus.Confirmed);
          }}
          applicantName={applicantName}
          contactName={contactName}
          equipmentDescription={equipmentDescription}
          requestedAmount={requestedAmount}
          dealerName={dealerName}
          dealerNotMatched={dealerNotMatched}
          selectedDealerDynamicsId={selectedDealerDynamicsId}
          onDealerChange={setSelectedDealerDynamicsId}
          formId={formId}
        />
      </Box>
    </PageContainer>
  );
};
